import { EDatasourceType, IActionDefinitionDto, IDatabaseTableModel, IDatasourceDto } from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import { DataDiagramSchemeService } from "../../../services/data-diagram-scheme-service";
import { DatasourceService } from "../../../services/datasource-service";
import { KMainFunctions } from "../../../shared/utility/main-functions";

/**
 * Creating a datasource, a table or an action — the flows themselves, with no UI attached.
 *
 * They were the submit handlers of two screens, both retired: the three-column "Add a new
 * Datasource" page (`create-connection.tsx`) and the table naming page (`create-table-default.tsx`
 * — a new table is named in the table editor's own bar now). Pulled out here so every surface runs
 * the same rules — the checks, the messages and the provider calls are the originals, not a second
 * implementation of them.
 */

/** Name rule the datasource screen has always used. Digits and spaces are allowed despite the message. */
export const DATASOURCE_NAME_PATTERN: RegExp = /^[a-zA-Z0-9 ]+$/;
export const DATASOURCE_NAME_MESSAGE: string = "Field accepts only english letters!";

/** Table names go into SQL, so they are identifiers: letter or underscore first. */
export const TABLE_NAME_PATTERN: RegExp = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
export const TABLE_NAME_MESSAGE: string =
  "Table name must start with a letter or underscore and can only contain letters, numbers and underscores.";

/** Action names become generated method names. */
export const ACTION_NAME_PATTERN: RegExp = /^[A-Za-z][A-Za-z0-9_]+$/;
export const ACTION_NAME_MESSAGE: string =
  "Action name must start with a letter and can only contain letters, numbers and underscores.";

/**
 * The four types whose datasource is saved immediately and opens straight into an action
 * editor with a pre-named first action. Everything else defers the save to its own connection
 * panel. Only Blockly is reachable from the picker today; the AI three are created from AI
 * Functions, and are kept here because the save path is shared.
 */
const IMMEDIATE_SAVE_TYPES: EDatasourceType[] = [
  EDatasourceType.AIActions,
  EDatasourceType.Blockly,
  EDatasourceType.AIAgent,
  EDatasourceType.VisionAction
];

const getImmediateSaveLabel = (type: EDatasourceType): string => {
  if (type === EDatasourceType.AIActions) return "AI Actions";
  if (type === EDatasourceType.AIAgent) return "AI Agents";
  if (type === EDatasourceType.VisionAction) return "Vision Action";
  return "Blockly Actions";
};

const getFirstActionName = (type: EDatasourceType): string => {
  if (type === EDatasourceType.AIActions) return "AI_Action1";
  if (type === EDatasourceType.AIAgent) return "AI_Agent1";
  if (type === EDatasourceType.VisionAction) return "Vision_Action1";
  return "Blockly_Action1";
};

/**
 * Rejects a datasource name before anything is created: an existing datasource's name, or the
 * name its own first child action would take (an AI/Blockly datasource and its action would
 * otherwise collide).
 */
export const validateNewDatasourceName = (
  sd: IScreenDesignContextState,
  type: EDatasourceType,
  name: string
): string | undefined => {
  const candidate: string = (name ?? "").toLowerCase();
  if (candidate.length === 0) return undefined;

  const isEqualToDatasourceName: IDatasourceDto | undefined = sd?.datasources?.find(
    (datasource: IDatasourceDto): boolean => datasource.name.toLowerCase() === candidate
  );
  if (isEqualToDatasourceName) return "Datasource name already exists";

  if (type === EDatasourceType.AIActions && candidate === "ai_action1") {
    return "Datasource name cannot be the same as its child actions' name.";
  }
  if (type === EDatasourceType.Blockly && candidate === "blockly_action1") {
    return "Datasource name cannot be the same as its child actions' name.";
  }
  return undefined;
};

export interface ICreateDatasourceInput {
  sd: IScreenDesignContextState;
  type: EDatasourceType;
  name: string;
  /** C# only: the template the picker had selected, which decides the seeded class. */
  template?: EDatasourceType;
  /**
   * A datasource saved on the spot opens its first action editor. Given this, the caller decides
   * WHEN: it receives that step and runs it itself — the picker holds its ✓ first, so the sheet
   * does not vanish under a confirmation nobody saw. Omitted, the editor opens at once.
   */
  openCreated?: (open: () => void) => void;
}

/**
 * `connection-panel` — the type's own panel is now open and will do the saving.
 * `created` — the datasource exists and its first action editor is open.
 * `rejected` — a name check refused it; the caller keeps its sheet open.
 */
export type CreateDatasourceOutcome = "connection-panel" | "created" | "rejected";

export const createDatasource = async (input: ICreateDatasourceInput): Promise<CreateDatasourceOutcome> => {
  const { sd, type, name } = input;
  if (!name) return "rejected";

  const nameError: string | undefined = validateNewDatasourceName(sd, type, name);
  if (nameError) {
    KMainFunctions.displayWarningNotification(nameError);
    return "rejected";
  }

  try {
    if (!IMMEDIATE_SAVE_TYPES.includes(type)) {
      // The template prefetch ran before the name existed; refetch so the seeded class is named
      // after the datasource before the C# editor opens with this code. Refetch the TEMPLATE THE
      // USER SELECTED — hardcoding CSharpFunctions here would clobber a chosen provider template
      // with the plain-class one (losing its ": IProvider" base list).
      if (type === EDatasourceType.CSharpFunctions) {
        const templateType: EDatasourceType = input.template ?? EDatasourceType.CSharpFunctions;
        const templateResponse = await DatasourceService.GetCSharpCodeTemplates(templateType, name);
        if (templateResponse.status === 200) {
          sd.setActiveCreatingCSharpCode(templateResponse.data, templateType);
        }
      }
      sd.setActiveCreatingDatasourceName(type, name);
      return "connection-panel";
    }

    const res = await DatasourceService.SaveDatasource({ name, datasourceType: type } as IDatasourceDto);
    if (res.status !== 200) {
      throw new Error(`Something went wrong while Creating ${getImmediateSaveLabel(type)}  Datasource`);
    }

    const datasource: IDatasourceDto | undefined = res.data.find((item: IDatasourceDto): boolean => item.name === name);
    const open = (): void => {
      sd.setEditingActionsDatasource(datasource, undefined, true, false, undefined, getFirstActionName(type));
    };
    sd.refreshDatabases();
    sd.refreshActions();
    if (input.openCreated) {
      input.openCreated(open);
    } else {
      open();
    }
    // No toast: the new datasource's own editor opening IS the result (and the picker has shown its ✓).
    return "created";
  } catch (error) {
    KMainFunctions.exceptionHandler(error);
    return "rejected";
  }
};

/**
 * The tables a datasource already has, as far as the designer knows them. The managed schema's
 * loaded copy (`databaseDiagramData`) when it is there; the schema map otherwise — it can be the
 * slim one (`getTableIds` fills it with column-less rows), which is still enough for names. The
 * table editor's switcher lists its tables from the same two, in the same order.
 */
export const getKnownTables = (sd: IScreenDesignContextState, datasource?: IDatasourceDto): IDatabaseTableModel[] => {
  const loaded: IDatabaseTableModel[] = sd?.databaseDiagramData?.leftMenuData?.tables ?? [];
  if (loaded.length > 0) return loaded;
  return datasource ? (sd?.schemas?.get(datasource.id)?.tables ?? []) : [];
};

/**
 * The two local checks a new table's name answers on top of the pattern: an existing table, and a
 * screen or process of the same name — both become generated identifiers. Case-insensitive.
 *
 * Cheap enough to answer on every keystroke, which is why the server's reserved-key check is
 * NOT here: see `isTableNameReserved`.
 */
export const validateNewTableName = (
  sd: IScreenDesignContextState,
  datasource: IDatasourceDto | undefined,
  name: string
): string | undefined => {
  const candidate: string = (name ?? "").toLowerCase();
  if (candidate.length === 0) return undefined;

  const isTableExist: boolean = getKnownTables(sd, datasource).some(
    (table: IDatabaseTableModel): boolean => (table.name ?? "").toLowerCase() === candidate
  );
  if (isTableExist) return "Table name already exist.";

  const isEqualToScreenName: boolean = (sd?.screenList ?? []).some(
    (screen): boolean => (screen.name ?? "").toLowerCase() === candidate
  );
  const isEqualToWfName: boolean = (sd?.workFlowList ?? []).some(
    (workflow): boolean => (workflow.name ?? "").toLowerCase() === candidate
  );
  if (isEqualToScreenName || isEqualToWfName) {
    return "Table name can not be equal to screen name or process name.";
  }

  return undefined;
};

/**
 * The server's own reserved-key list, asked once on SUBMIT — an antd validator runs on every
 * change, and this is an HTTP call: typing a nine-letter table name would post nine times.
 * The table editor's CREATE asks the same list at the same moment, over the whole schema it sends.
 */
export const isTableNameReserved = async (name: string): Promise<boolean> => {
  const table = {} as IDatabaseTableModel;
  table.id = Guid.create().toString();
  table.name = name;
  table.dropped = false;
  table.columns = [];
  const res = await DataDiagramSchemeService.CheckSchemaReservedKeyUsed({ tables: [table] });
  return res.status !== 200;
};

/** An action name is unique within its own datasource; the comparison is case-insensitive. */
export const validateNewActionName = (
  actions: IActionDefinitionDto[],
  datasourceId: string,
  name: string
): string | undefined => {
  const candidate: string = (name ?? "").toLowerCase();
  if (candidate.length === 0) return undefined;
  const exists: IActionDefinitionDto | undefined = (actions ?? []).find(
    (action: IActionDefinitionDto): boolean =>
      action.kDatasource_Id === datasourceId && (action.name ?? "").toLowerCase() === candidate
  );
  return exists ? "Action name already exists in this datasource." : undefined;
};
